import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../environments/environment";
import { HelperService } from "./helper.service";
import { StorageKeys, StorageService } from "./storage.service";
import { UserService } from "./user.service";
import { catchError, map, take } from "rxjs/operators";
import { Observable, of as observableOf, of, ReplaySubject, Subject, throwError } from "rxjs";
import moment from "moment";
import { WindowService } from "./window.service";
import { ProductService } from "./product.service";
import { CurrencyPipe } from "@angular/common";
import { ProductLicenseItem, SimpleProduct } from "../models";
import { ContentService } from "./content.service";
import { TranslateService } from "@ngx-translate/core";

// Countries where Avalara needs a state/province to calculate tax (US sales tax, Canadian
// GST/HST/PST). Everywhere else the state is optional on the billing address.
export const STATE_REQUIRED_COUNTRIES = ["US", "CA"];

// Postal-code formats checked before tax is calculated, so an address like "FL 225370" is
// caught at the form instead of failing at checkout (paeapi#1176).
export const POSTAL_CODE_PATTERNS: Record<string, RegExp> = {
  US: /^\d{5}(-\d{4})?$/,
  CA: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
};

@Injectable({
  providedIn: "root",
})
export class CheckoutService {
  public checkoutComplete$: Subject<any> = new Subject<any>();
  public productTitle: string;
  public total: any = 0;

  public cardBrandIcons: any = {
    amex: "./assets/icons/cc-icons/amex.png",
    visa: "./assets/icons/cc-icons/visa.png",
    discover: "./assets/icons/cc-icons/discover.png",
    unionpay: "./assets/icons/cc-icons/unionpay.png",
    mastercard: "./assets/icons/cc-icons/mastercard.png",
    generic: "./assets/icons/cc-icons/credit_card.png",
  };

  public isReadyForCheckout$: Subject<any> = new Subject<any>();
  public purchaseComplete$: Subject<any> = new Subject<any>();
  public stripe: any;
  public stripeCustomerId: any;
  public s1PlusPrices: {
    monthly?: {
      id: string;
      price: number;
      currency: string;
      formatted_price: string;
      tax: number;
      tax_rate?: number;
      productId: any;
      stripeProductId?: string;
      title: string;
      image?: string;
    };
    annual?: {
      id: string;
      price: number;
      currency: string;
      formatted_price: string;
      tax: number;
      tax_rate?: number;
      productId: any;
      stripeProductId?: string;
      title: string;
      image?: string;
    };
    hybrid_upgrade?: {
      id: string;
      price: number;
      currency: string;
      formatted_price: string;
      tax: number;
      tax_rate?: number;
      stripeProductId?: string;
      title: string;
      image?: string;
    };
  };
  calculatingTax: boolean = false;
  couponApplied: {
    code?: string;
    coupon_id?: string;
    discount_percent?: number;
    amount_off?: number;
    title?: string;
  };
  public coupons: any;
  public coupon: any;
  public couponData: any;
  // Promotion coupon (with discount magnitude) carried over from a "Special Offers" CTA,
  // used to compare against the auto-apply active offer and favor the better deal.
  public promotionCoupon: { code: string; percent_off?: number; amount_off?: number; currency?: string };
  public showCouponField: boolean;
  public discountSuccessMessage: string;
  public couponParam: any;
  public applyingCoupon: boolean;
  activeFinalPaymentAmount: number;
  public billingInfoChange$ = new Subject<any>();
  public billingInfo: any = {
    firstName: this.userService.user?.firstName || "",
    lastName: this.userService.user?.lastName || "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: this.userService.user?.country || this.userService.user?.detectedCountry || "US",
  };
  public taxedBillingInfo: any;

  public ccInfo: any = {
    cardNumber: false,
    cardExpiry: false,
    cardCVC: false,
  };
  public cardCountry: any;
  public stripeCustomerAddress: any;
  public loadingCheckout: boolean = false;
  public loadingPrices: boolean;
  public loadingPaymentMethods: boolean;
  public loadingMsg: string;
  public percentDone: number;
  public taxCalculated: number;
  public totalCharge: number;

  public activeCurrency: any;
  public currencyConverted: boolean;
  public selectedS1PlusPlan: string = "monthly";
  public activeOffers: any;
  public selectedActiveOffer: any;
  public offersRetrieved: boolean = false;
  private fieldsInitiallyCompleted = false; // Introduced flag

  public supportedCurrencies: Array<any>;
  public priceList: Array<any>;
  public discountedMonthlyDisplayPrice: any;
  public discountedAnnualDisplayPrice: any;
  public discountedAnnualMonthlyDisplayPrice: any;
  public applicableActiveOffer: any;
  public monthlyDisplayPrice: any;
  public annualDisplayPrice: any;
  public annualMonthlyDisplayPrice: any;
  public hybridUpgradePrice: any;
  public selectedPrice: any;
  public isTaxIncluded: boolean = false;
  public currencyIsConverted: boolean = false;
  // Set when Avalara rejects the billing address in the tax preview; blocks checkout until the address changes.
  public billingAddressRejected: boolean = false;
  // Tax previews can overlap while the customer edits the address; only the latest one may set state.
  private taxPreviewSeq = 0;

  public subscriptionId: any;
  public clientSecret: any;

  public paymentMethods: Array<any>;
  public newPaymentMethod: any;
  public selectedExistingPaymentMethod: any;
  public addNewCard: boolean;

  public source: string;

  public checkoutError: any;
  public orderCompletedTrackingArgs: any;

  public selectedProduct: any;
  public selectedProductUpgrade: any;
  public genericProduct: any;
  public upgradingProduct: any;
  public upgradableProducts: any;
  public upgradableNotionProducts: any;
  public upgradableStudioOneProducts: any;

  public transferableProduct: any;

  public scheduleDetails: any;

  public productTransferDetails: {
    transferingLicense: ProductLicenseItem;
    transferingProduct: SimpleProduct;
  };

  public priceIncludesDiscount: any;

  constructor(
    private http: HttpClient,
    private _helperService: HelperService,
    private userService: UserService,
    private storage: StorageService,
    public win: WindowService,
    public productService: ProductService,
    private currencyPipe: CurrencyPipe,
    public contentService: ContentService,
    private translate: TranslateService
  ) {
    this.userService.user$.subscribe((user) => {
      this.stripeCustomerId = user.stripe_id;
    });
  }

  initCheckout(args: { stripe: any; title: string; couponParam?: string; source?: string; productUpgrade?: any; creatingPaymentMethod?: boolean; genericProduct?: any; scheduleDetails?: any }) {
    this.upgradableProducts = args.title.toLowerCase().indexOf("notion") > -1 ? this.upgradableNotionProducts : this.upgradableStudioOneProducts;
    const storedPromotion = this.storage.getItem(StorageKeys.PROMOTION_COUPON);
    if (storedPromotion) {
      this.promotionCoupon = storedPromotion;
      this.storage.removeItem(StorageKeys.PROMOTION_COUPON);
    }
    let storedCoupons = this.storage.getItem(StorageKeys.CHECKOUT_COUPONS);
    if (storedCoupons && storedCoupons.length) {
      this.coupon = storedCoupons[0];
      this.storage.removeItem(StorageKeys.CHECKOUT_COUPONS);
      this.handleOffersAndCoupons();
    }
    this.loadingCheckout = true;
    this.productTitle = args.title;
    if (args.couponParam) this.couponParam = args.couponParam;
    if (args.source) this.source = args.source;
    if (args.productUpgrade) this.selectedProductUpgrade = args.productUpgrade;
    if (args.genericProduct) this.genericProduct = args.genericProduct;
    if (args.scheduleDetails) this.scheduleDetails = args.scheduleDetails;
    if (!this.selectedS1PlusPlan) this.selectedS1PlusPlan = "monthly";
    return new Promise((resolve, reject) => {
      this.stripe = args.stripe;
      this.setAddress().then(() => {
        if (this.userService.user?.stripe_id && !args.creatingPaymentMethod && environment.features.pay_with_saved_card) {
          this.loadCustomerPaymentMethods().then((result) => {
            this.loadingCheckout = false;
            resolve(true);
          });
        } else {
          this.loadingCheckout = false;
          resolve(true);
        }
      });
    });
  }

  getUpgradableProducts(force?, type = "Studio One") {
    let url = environment.paeApiUrl + `user/get-upgradable-products/${type}`;

    if (type == "Notion") {
      if (this.upgradableNotionProducts && !force) {
        return of(this.upgradableNotionProducts);
      } else {
        return this.http.get(url, this._helperService.getHttpOptions()).pipe(
          map((result: any) => {
            if (result && result.success && result.data) {
              this.upgradableNotionProducts = result;
              return result;
            }
          })
        );
      }
    } else {
      if (this.upgradableStudioOneProducts && !force) {
        return of(this.upgradableStudioOneProducts);
      } else {
        return this.http.get(url, this._helperService.getHttpOptions()).pipe(
          map((result: any) => {
            if (result && result.success && result.data) {
              this.upgradableStudioOneProducts = result;
              return result;
            }
          })
        );
      }
    }
  }

  loadCustomerPaymentMethods() {
    return new Promise((resolve, reject) => {
      if (this.userService.user?.stripe_id) {
        this.loadingPaymentMethods = true;
        this.userService.getPaymentMethods().subscribe({
          next: (result: any) => {
            if (this.userService.paymentMethods) {
              this.stripeCustomerAddress = result.address;
              this.loadingPaymentMethods = false;
              if (this.userService.paymentMethods.length) {
                resolve(true);
                this.win.setTimeout(() => {
                  this.selectPaymentMethod(this.userService.paymentMethods[0]);
                }, 500);
              } else {
                resolve(true);
              }
            } else {
              resolve(true);
              this.loadingPaymentMethods = false;
            }
          },
          error: (error) => {
            resolve(true);
            this.loadingPaymentMethods = false;
          },
        });
      } else {
        resolve(true);
      }
    });
  }

  selectPaymentMethod(paymentMethod) {
    if (!this.selectedExistingPaymentMethod || this.selectedExistingPaymentMethod != paymentMethod.id) {
      this.selectedExistingPaymentMethod = paymentMethod.id;
      this.totalCharge = undefined;
      this.loadCountryOfCard(paymentMethod);
      this.addNewCard = false;
    }
  }

  addNewPaymentMethod() {
    if (!this.addNewCard) {
      this.selectedExistingPaymentMethod = undefined;
      this.billingInfo = {
        ...this.billingInfo,
        address: "",
        city: "",
        state: "",
        zip: "",
        country: this.userService.user?.country || this.userService.user?.detectedCountry || "US",
      };
      this.loadPriceByCountry(this.billingInfo.country);
      this.addNewCard = true;
    } else {
      this.addNewCard = false;
    }
  }

  onBillingInfoChange(property: string, newValue: any) {
    const previousState = this.billingInfo.state;
    const previousCountry = this.billingInfo.country;

    this.billingInfo[property] = newValue;
    if (this.billingAddressRejected && this.checkoutError?.type === "billing_info") this.checkoutError = undefined;
    this.billingAddressRejected = false;

    let args: any = { billingInfo: this.billingInfo, fieldChanged: property, newValue: newValue };

    if (this.allFieldsPresent()) {
      if (!this.fieldsInitiallyCompleted) {
        this.fieldsInitiallyCompleted = true; // Set the flag true once all fields are filled out the first time
        args.shouldRecalculateTax = true;
      } else if ((property === "state" && previousState !== newValue) || (property === "country" && previousCountry !== newValue) || (property === "zip" && previousCountry !== newValue)) {
        args.shouldRecalculateTax = true;
      }
    }

    if (property === "country" && newValue != previousCountry) {
      this.billingInfo.state = "";
    }
    if (args.fieldChanged == "country") {
      this.loadPriceByCountry(this.billingInfo.country).then(() => {
        if (args.shouldRecalculateTax) this.calculateFinalAmount();
      });
    } else {
      if (args.shouldRecalculateTax) this.calculateFinalAmount();
    }
    this.billingInfoChange$.next(args);
  }

  public hasSavedAddress: boolean = false;
  setAddress() {
    return new Promise((resolve, reject) => {
      this.billingInfo.firstName = this.userService.user?.firstName ?? "";
      this.billingInfo.lastName = this.userService.user?.lastName ?? "";
      this.userService.getAddresses().subscribe({
        next: (addresses: any) => {
          if (addresses && addresses.length) {
            let bestAddress = addresses.find((a) => a.zipcode);
            if (bestAddress) {
              this.hasSavedAddress = true;
              this.billingInfo.address = bestAddress.street;
              this.billingInfo.city = bestAddress.city;
              this.billingInfo.state = bestAddress.state;
              this.billingInfo.zip = bestAddress.zipcode;
              this.billingInfo.country = bestAddress.countryCode;
            }
          }
          if (!this.billingInfo.zip && this.userService.user?.stripe_customer?.address) {
            this.billingInfo.zip = this.userService.user.stripe_customer.address.postal_code;
            this.billingInfo.state = this.userService.user.stripe_customer.address.state;
            this.billingInfo.city = this.userService.user.stripe_customer.address.city;
            this.billingInfo.country = this.userService.user.stripe_customer.address.country;
            this.billingInfo.address = this.userService.user.stripe_customer.address.line1;
            this.userService
              .saveAddress({
                street: this.billingInfo.address,
                city: this.billingInfo.city,
                state: this.billingInfo.state,
                zipcode: this.billingInfo.zip,
                countryCode: this.billingInfo.country,
              })
              .subscribe();
          }
          resolve(true);
        },
        error: (err) => {
          resolve(true);
        },
      });
    });
  }

  getCouponCode(code) {
    const url = this.userService.usePaeapiCheckout() ? environment.paeApiUrl + "checkout/coupon/" + code : environment.apiUrl + "stripe/coupon_code/" + code;
    return this.http.get(url, this._helperService.getHttpOptions());
  }

  allFieldsPresent() {
    const requiredFields = ["firstName", "lastName", "address", "city", "country"];

    if (this.isStateRequired(this.billingInfo.country)) {
      requiredFields.push("state", "zip");
    }

    const present = requiredFields.every((field) => {
      const value = this.billingInfo[field];
      return !!value && !!value.toString().trim();
    });
    return present && this.isPostalCodeValid(this.billingInfo.country, this.billingInfo.zip);
  }

  get billingStateRequired(): boolean {
    return this.isStateRequired(this.billingInfo?.country);
  }

  get billingPostalCodeValid(): boolean {
    return this.isPostalCodeValid(this.billingInfo?.country, this.billingInfo?.zip);
  }

  isStateRequired(country: string): boolean {
    return STATE_REQUIRED_COUNTRIES.includes(country);
  }

  // True when the postal code is empty or matches the country's format (countries without a pattern always pass).
  isPostalCodeValid(country: string, zip: string): boolean {
    const pattern = POSTAL_CODE_PATTERNS[country];
    const value = (zip || "").toString().trim();
    return !pattern || !value || pattern.test(value);
  }

  loadPrices(prices, data, applyCouponParam = true) {
    this.priceList = prices;
    this.loadDefaultPrices(true);
    if (!this.hasSavedAddress || !this.billingInfo.country) this.billingInfo.country = this.userService.user?.country || data.geo["country-code"] || "US";
    this.supportedCurrencies = data.supported_currencies || [];
    this.activeCurrency = this.replaceCurrency(data.geo["currency-code"] ? data.geo["currency-code"] : "USD");
    return new Promise((resolve, reject) => {
      this.loadPriceByCountry().then((result) => {
        if (applyCouponParam) this.applyCouponParam();
        if (!this.couponParam && this.coupon && !this.couponData && this.productTitle !== environment.studioOnePlusBrandName) this.applyCoupon();
        resolve(true);
      });
    });
  }

  handleOffersAndCoupons() {
    if (environment.features.active_offers && !this.coupons) {
      if (this.offersRetrieved) {
        this.displayDiscountedOfferPrice();
      } else {
        this.getActiveOffers();
      }
    } else {
      this.applyHardwareCoupon();
    }
    if (this.couponParam && this.isValidCheckout) {
      this.coupon = this.couponParam;
    }
  }

  loadGenericProductPrices(stripeProductId) {
    return this.userService.getGenericProductPrices(stripeProductId).pipe(
      map((result) => {
        return result;
      })
    );
  }

  loadS1PlusPrices(term = "monthly") {
    this.loadingPrices = true;
    return new Promise((resolve, reject) => {
      this.userService.getSpherePricesStripe().subscribe({
        next: (result: any) => {
          if (result?.prices) {
            let subscriptionPrices = result.prices.filter((p) => p.product === environment.stripe.subscription_product.monthly || p.product === environment.stripe.subscription_product.annual);
            let annualScheduleUpradePrices = result.prices.filter((p) => p.product === environment.stripe.subscription_product.hybrid_upgrade);
            this.loadPrices([...subscriptionPrices, ...annualScheduleUpradePrices], result, false).then(() => {
              if (this.s1PlusPrices?.annual?.price) {
                this.activeCurrency = this.s1PlusPrices.annual.currency;
                this.monthlyDisplayPrice = this.s1PlusPrices.monthly.price;
                this.annualDisplayPrice = this.s1PlusPrices.annual.price;
                this.hybridUpgradePrice = this.s1PlusPrices.hybrid_upgrade.price;
                this.annualMonthlyDisplayPrice = (this.s1PlusPrices.annual.price - this.s1PlusPrices.annual.tax) / 12;
              }
              this.selectPlan(term, true);
              resolve(result);
            });
          }
        },
      });
    });
  }

  loadPriceByCountry(country?) {
    return new Promise((resolve, reject) => {
      this.loadingPrices = true;
      this.taxCalculated = undefined;
      if (country) this.billingInfo.country = country;
      if (!this.priceList) {
        this.loadingPrices = false;
        resolve(true);
        return;
      }
      this.userService.getCurrencyCodeByCountryCode(this.billingInfo.country).subscribe((result: any) => {
        if (result.data && result.data.locations && result.data.locations.length && result.data.locations[0]["country-code"].toLowerCase() === this.billingInfo.country.toLowerCase()) {
          this.activeCurrency = this.replaceCurrency(result.data.locations[0]["currency-code"]);
          //Filter from existing stripe prices
          if (!this.priceList) {
            this.loadingPrices = false;
            resolve(true);
            return;
          }
          let filteredPrices = this.priceList.filter((p) => p.currency.toLowerCase() === this.activeCurrency.toLowerCase());
          if (filteredPrices && filteredPrices.length) {
            this.currencyIsConverted = false;
            if (this.productTitle == environment.studioOnePlusBrandName) {
              // this sets the studio one+ prices objects (this.s1PlusPrices) with the monthly and annual prices
              filteredPrices.map((price) => {
                if (price.recurring?.interval === "year" || price.recurring?.interval === "month") {
                  this.setS1PlusPrices(
                    price.recurring.interval === "month" ? "monthly" : "annual",
                    price.id,
                    this.isNoDecimalCurrency(price.currency) ? price.unit_amount : price.unit_amount / 100,
                    price.currency
                  );
                } else {
                  this.setS1PlusPrices(null, price.id, this.isNoDecimalCurrency(price.currency) ? price.unit_amount : price.unit_amount / 100, price.currency);
                }
              });
            } else if (this.selectedProductUpgrade) {
              /**
               * this sets the selected product to the price for the user's country
               * INFO: If this is the product to upgrade
               */
              const price = filteredPrices.find((p) => p.product === this.selectedProductUpgrade.stripeProductId);
              this.setSelectedProduct(this.selectedProductUpgrade, price.product, this.isNoDecimalCurrency(price.currency) ? price.unit_amount : price.unit_amount / 100, price.currency);
            } else if (this.genericProduct) {
              this.setSelectedProduct(
                this.genericProduct,
                filteredPrices[0].product,
                this.isNoDecimalCurrency(filteredPrices[0].currency) ? filteredPrices[0].unit_amount : filteredPrices[0].unit_amount / 100,
                filteredPrices[0].currency
              );
            }
            this.handleOffersAndCoupons();
            this.setTotalAmountPayable();
            resolve(true);
          } else if (this.activeCurrency && this.isCurrencySupported(this.activeCurrency.toLowerCase())) {
            // Converting the price as price for current currency is not available
            this.convertCurrency().then(() => {
              resolve(true);
            });
          } else {
            this.loadDefaultPrices();
            resolve(true);
          }
        } else {
          this.activeCurrency = "USD";
          this.loadingPrices = false;
          this.loadDefaultPrices();
          resolve(true);
        }
      });
    });
  }

  convertCurrency() {
    return new Promise((resolve, reject) => {
      if (!this.priceList) {
        resolve(true);
        return;
      }
      let basePrices = this.priceList.filter((p) => p.currency === "usd");
      let currencyData = {
        currency_code: this.activeCurrency,
        convert: [],
      };
      if (basePrices && basePrices.length) {
        basePrices.map((price) => {
          currencyData.convert.push({
            from: "usd",
            priceId: price.id,
            value: (price.unit_amount / 100).toFixed(2),
            [price.recurring && price.recurring.interval ? "interval" : "product"]: price.recurring && price.recurring.interval ? price.recurring.interval : price.product,
          });
        });
        this.userService.currencyConvert(currencyData).subscribe((result: any) => {
          if (result.success && result.data.every((c) => c.valid === true)) {
            this.currencyIsConverted = true;
            if (this.productTitle == environment.studioOnePlusBrandName) {
              result.data.map((price) => {
                if (price.interval === "year" || price.interval === "month") {
                  this.setS1PlusPrices(
                    price.interval === "month" ? "monthly" : "annual",
                    "converted_" + price.interval,
                    this.isNoDecimalCurrency(price["to-type"]) ? parseInt(price["result-float"]) : Number(price["result-float"].toFixed(2)),
                    price["to-type"]
                  );
                } else {
                  this.setS1PlusPrices(
                    null,
                    "converted_" + price.id,
                    this.isNoDecimalCurrency(price["to-type"]) ? parseInt(price["result-float"]) : Number(price["result-float"].toFixed(2)),
                    price["to-type"]
                  );
                }
              });
            } else if (this.selectedProductUpgrade) {
              const filteredProductPrice = result.data.find((p) => p.product === this.selectedProductUpgrade.stripeProductId);
              this.setSelectedProduct(
                this.selectedProductUpgrade,
                "converted_" + filteredProductPrice.product,
                this.isNoDecimalCurrency(filteredProductPrice["to-type"]) ? parseInt(filteredProductPrice["result-float"]) : Number(filteredProductPrice["result-float"].toFixed(2)),
                filteredProductPrice["to-type"]
              );
            } else if (this.genericProduct) {
              const filteredProductPrice = result.data.find((p) => p.product === this.genericProduct.stripeProductId);
              this.setSelectedProduct(
                this.genericProduct,
                "converted_" + filteredProductPrice.product,
                this.isNoDecimalCurrency(filteredProductPrice["to-type"]) ? parseInt(filteredProductPrice["result-float"]) : Number(filteredProductPrice["result-float"].toFixed(2)),
                filteredProductPrice["to-type"]
              );
            }
            this.handleOffersAndCoupons();
            this.loadingPrices = false;
            this.clientSecret = "";
            this.subscriptionId = "";
          } else {
            this.loadingPrices = false;
            this.loadDefaultPrices();
          }
          this.setTotalAmountPayable();
          resolve(true);
        });
      } else {
        this.loadingPrices = false;
        this.setTotalAmountPayable();
        resolve(true);
      }
    });
  }

  loadDefaultPrices(init = false) {
    if (!this.priceList) {
      return;
    }
    let basePrices = this.priceList.filter((p) => p.currency === "usd");
    this.currencyIsConverted = false;
    if (this.productTitle == environment.studioOnePlusBrandName) {
      basePrices.map((price) => {
        if (price.recurring?.interval === "year" || price.recurring?.interval === "month") {
          this.setS1PlusPrices(price.recurring.interval === "month" ? "monthly" : "annual", price.id, price.unit_amount / 100, price.currency);
        } else {
          this.setS1PlusPrices(null, price.id, price.unit_amount / 100, price.currency);
        }
      });
    } else if (this.selectedProductUpgrade) {
      let filteredProductPrice = basePrices.find((p) => p.product === this.selectedProductUpgrade.stripeProductId);
      this.setSelectedProduct(
        this.selectedProductUpgrade,
        filteredProductPrice.product,
        this.isNoDecimalCurrency(filteredProductPrice.currency) ? filteredProductPrice.unit_amount : filteredProductPrice.unit_amount / 100,
        filteredProductPrice.currency
      );
    } else if (this.genericProduct) {
      this.setSelectedProduct(
        this.genericProduct,
        basePrices[0].product,
        this.isNoDecimalCurrency(basePrices[0].currency) ? basePrices[0].unit_amount : basePrices[0].unit_amount / 100,
        basePrices[0].currency
      );
    }

    if (!init) {
      this.handleOffersAndCoupons();
      this.setTotalAmountPayable();
    }
  }

  loadCountryOfCard(paymentMethod?, card?, channel?) {
    this.loadingPrices = true;
    if (paymentMethod) {
      this.cardCountry = paymentMethod.card.country;
      //If card country doesn't match with user location
      if (this.billingInfo.country && this.billingInfo.country !== paymentMethod.card.country) {
        this.setAddressBySelectedPaymentMethod(paymentMethod);
        this.loadPriceByCountry(this.billingInfo.country);
      } else {
        this.setAddressBySelectedPaymentMethod(paymentMethod);
        if (!this.activeCurrency) {
          this.userService.getCurrencyCodeByCountryCode(paymentMethod.card.country).subscribe((result: any) => {
            if (result.data && result.data.locations && result.data.locations.length && result.data.locations[0]["country-code"].toLowerCase() === this.billingInfo.country.toLowerCase()) {
              this.activeCurrency = result.data.locations[0]["currency-code"];
              this.calculateFinalAmount();
            }
          });
        } else {
          this.calculateFinalAmount();
        }
      }
    } else {
      this.stripe
        .createPaymentMethod({
          type: "card",
          card: card,
          billing_details: { name: this.billingInfo.firstName + " " + this.billingInfo.lastName },
          metadata: { sales_channel: channel },
        })
        .then((response) => {
          if (response.paymentMethod) {
            this.cardCountry = response.paymentMethod.card.country;
            this.newPaymentMethod = response.paymentMethod.id;
            if (this.couponParam) {
              this.coupon = this.couponParam;
              this.applyCoupon();
            }
            if (this.billingInfo.country !== response.paymentMethod.card.country) {
              //If card country doesn't match with user location
              this.billingInfo.country = response.paymentMethod.card.country;
              this.loadPriceByCountry(this.billingInfo.country);
            } else {
              this.loadingPrices = false;
              this.calculateFinalAmount();
            }
          } else {
            this.loadingPrices = false;
          }
        })
        .catch((err) => {
          this.loadingPrices = false;
        });
    }
  }

  calculateFinalAmount() {
    if ((this.coupon || this.couponParam) && this.isValidCheckout) {
      if (this.couponParam) {
        this.coupon = this.couponParam;
        this.showCouponField = true;
      }
    }
    this.setChargedMessage();
    this.setTotalAmountPayable();
    this.win.setTimeout(() => {
      this.applyCoupon();
    }, 1000);
  }

  setChargedMessage() {
    if (this.scheduleDetails && !this.s1PlusPrices) return;
    this.isTaxIncluded = this.currencyIsConverted || this.billingInfo.country === "US" ? false : true;
    let selectedPrice = this.selectedPrice?.price || this.selectedProduct?.price;
    if (this.scheduleDetails) {
      selectedPrice = this.s1PlusPrices.hybrid_upgrade.price;
    }
    if (this.billingInfo && this.billingInfo.zip && selectedPrice) {
      let amount = (this.couponData ? selectedPrice - this.couponData["coupons-total"] : selectedPrice).toFixed(2);
      if (this.scheduleDetails) amount = this.s1PlusPrices.hybrid_upgrade.price.toFixed(2);
      let taxData = {
        lines: [
          {
            number: 1,
            quantity: 1,
            amount: amount,
            taxCode: environment.tax.tax_code,
            taxIncluded: this.isTaxIncluded,
          },
        ],
        date: new Date().toISOString().split("T")[0],
        customerCode: environment.tax.customer_code,
        address: {
          line1: this.billingInfo.address,
          city: this.billingInfo.city,
          region: this.billingInfo.state,
          country: this.billingInfo.country,
          postalCode: this.billingInfo.zip,
        },
        currencyCode: this.activeCurrency,
      };
      if (
        (this.billingInfo.country == "US" && !this.billingInfo.state) || // if they havent chosen a state, dont bother calculating tax yet.
        (this.billingInfo.country == "US" && !this.userService.stateMap[this.billingInfo.state]) // dont calculate tax if their chosen state is not in the US.
      ) {
        return;
      }
      if (this.allFieldsPresent()) {
        this.loadingPrices = true;
        this.calculatingTax = true;
        if (this.scheduleDetails && this.selectedS1PlusPlan == "monthly") {
          this.taxCalculated = 0;
          this.totalCharge = 0;
          this.loadingPrices = false;
          this.calculatingTax = false;
          this.setTotalAmountPayable();
        } else {
          const previewSeq = ++this.taxPreviewSeq;
          this.userService.calculateTax(taxData).subscribe({
            next: (result: any) => {
              if (previewSeq !== this.taxPreviewSeq) return;
              this.calculatingTax = false;
              if (this.billingAddressRejected) {
                this.billingAddressRejected = false;
                if (this.checkoutError?.type === "billing_info") this.checkoutError = undefined;
              }
              if (result) {
                this.taxedBillingInfo = { ...this.billingInfo };
                this.taxCalculated = result.totalTaxCalculated;
                if (!this.isTaxIncluded) {
                  this.totalCharge = Number((selectedPrice + this.taxCalculated).toFixed(2));
                } else {
                  this.totalCharge = 0;
                }
                this.loadingPrices = false;
                this.setTotalAmountPayable();
              }
            },
            error: (error) => {
              if (previewSeq !== this.taxPreviewSeq) return;
              this.calculatingTax = false;
              this.taxCalculated = 0;
              this.loadingPrices = false;
              this.setTotalAmountPayable();
              let errorMessage = this._helperService.retrieveErrorMessage(error);
              // paeapi returns Avalara's rejections (bad address, missing state, ...) as a 400. Block checkout
              // on any of them: the same address would fail again when the invoice is created (paeapi#1176).
              if (error?.status === 400 || errorMessage == "Address not geocoded.") {
                this.billingAddressRejected = true;
                const reason = errorMessage && errorMessage != "Address not geocoded." ? ` (${errorMessage})` : "";
                this.checkoutError = { type: "billing_info", message: this.translate.instant("checkout.check_billing_address") + reason };
              }
            },
          });
        }
      } else {
        this.setTotalAmountPayable();
        this.loadingPrices = false;
      }
    } else {
      this.setTotalAmountPayable();
      this.loadingPrices = false;
    }
  }

  setAddressBySelectedPaymentMethod(paymentMethod) {
    if (this.stripeCustomerAddress) {
      this.billingInfo.firstName = paymentMethod.billing_details.name?.split(" ")[0];
      this.billingInfo.lastName = paymentMethod.billing_details.name?.split(" ")[1];
      this.billingInfo.address = this.stripeCustomerAddress.line1;
      this.billingInfo.country = this.stripeCustomerAddress.country;
      this.billingInfo.city = this.stripeCustomerAddress.city;
      this.billingInfo.state = this.stripeCustomerAddress.state;
      this.billingInfo.zip = this.stripeCustomerAddress.postal_code;
    }
  }

  selectPlan(plan, initalSelect?) {
    if ((this.s1PlusPrices && plan !== this.selectedS1PlusPlan) || initalSelect) {
      this.selectedS1PlusPlan = plan;
      this.selectedPrice = this.s1PlusPrices[this.selectedS1PlusPlan];
      this.clientSecret = "";
      this.subscriptionId = "";
      this.totalCharge = 0;
      this.taxCalculated = 0;
      this.couponData = null;
      if (this.coupons && this.coupons.length) {
        this.applyHardwareCoupon(true, plan);
      } else if (this.isAutoOfferApplicable()) {
        this.applyActiveOffer(true);
        this.displayDiscountedOfferPrice();
      } else {
        this.applyCouponParam();
      }

      if (!this.isAutoOfferApplicable() && this.selectedActiveOffer) {
        // remove the selected active offer if it doesnt apply to this plan.
        this.removeActiveOffer();
      }

      this.calculateFinalAmount();
    }
  }

  removeActiveOffer() {
    this.discountedMonthlyDisplayPrice = undefined;
    this.discountedAnnualMonthlyDisplayPrice = undefined;
    this.discountedAnnualDisplayPrice = undefined;
    this.removeCoupon();
  }

  applyCoupon(promoCode?) {
    this.checkoutError = undefined;
    if (this.coupon || promoCode) {
      let code = promoCode ? promoCode : this.coupon;
      // getSelectedPrice() can be undefined for the S1Plus/subscription flow if it's called before
      // the plan price is resolved; fall back to the selected plan's price (how selectedPrice is set
      // in selectPlan()) so we always have a price to validate the coupon against.
      let selectedPrice = this.getSelectedPrice() || (this.selectedS1PlusPlan ? this.s1PlusPrices?.[this.selectedS1PlusPlan] : undefined);
      if (selectedPrice?.metadata?.discounted) {
        this.priceIncludesDiscount = selectedPrice.metadata;
        this.removeCoupon();
        return;
      } else {
        this.priceIncludesDiscount = undefined;
      }
      let coupon: any = {
        ...(this.selectedProduct && { type: "manual_invoice" }),
        promotion_code: code,
        address: {
          line1: this.billingInfo.address,
          city: this.billingInfo.city,
          state: this.billingInfo.state,
          country: this.billingInfo.country,
          postal_code: this.billingInfo.zip,
        },
      };
      if (this.currencyIsConverted || this.selectedPrice?.id.includes("converted")) {
        const currencyHere = selectedPrice?.currency ?? this.selectedProduct.currency;
        const priceHere = selectedPrice?.price ?? this.selectedProduct.price;
        coupon.price_data = {
          ...(!this.selectedProduct && {
            recurring: {
              interval: this.selectedS1PlusPlan.includes("annual") ? "year" : "month",
            },
          }),
          product: this.selectedProduct ? this.selectedProduct : environment.stripe.subscription_product[this.selectedS1PlusPlan],
          //currency: selectedPrice.currency ? selectedPrice.currency : this.selectedProduct.currency,
          currency: currencyHere,
          //unit_amount: this.isNoDecimalCurrency(selectedPrice.currency ? selectedPrice.currency : this.selectedProduct.currency) && !this.isDivisibleBy100(selectedPrice.currency ? selectedPrice.currency : this.selectedProduct.currency) ? selectedPrice.price ? selectedPrice.price: this.selectedProduct.price : parseInt((selectedPrice.price ? selectedPrice.price: this.selectedProduct.price * 100).toFixed(0)),
          unit_amount: this.isNoDecimalCurrency(currencyHere) && !this.isDivisibleBy100(currencyHere) ? priceHere : parseInt((priceHere * 100).toFixed(0)),
          tax_behavior: "unspecified",
        };
      } else {
        // Bugsnag Error: 6a738a00a531fed8b9f67b6c
        // getSelectedPrice() can return undefined before prices finish loading (or when no
        // price matches the active currency/product), so guard the dereference to avoid
        // "Cannot read properties of undefined (reading 'id')" when applying a coupon.
        coupon.price_id = selectedPrice?.id;
      }

      // Never send a coupon-validation request without a price — the API rejects it outright with
      // "price_id or price_data is required." before it can evaluate the code. Surface the standard
      // coupon error instead of a confusing backend message.
      if (!coupon.price_id && !coupon.price_data) {
        this.coupon = undefined;
        this.checkoutError = { type: "coupon_error", message: this.translate.instant("checkout.coupon_not_found") };
        return;
      }

      this.applyingCoupon = true;
      this.loadingPrices = true;
      this.userService.validateCoupon(coupon).subscribe({
        next: (result: any) => {
          this.applyingCoupon = false;
          this.loadingPrices = false;
          if (result) {
            let total = 0;
            if (result.total_discount_amounts[0] && result.total_discount_amounts[0].amount > 0) {
              if (this.isNoDecimalCurrency(result.currency) && !this.isDivisibleBy100(result.currency)) {
                total = result.total_discount_amounts[0].amount;
              } else {
                total = Number((result.total_discount_amounts[0].amount / 100).toFixed(2));
              }
            }
            // this.selectedActiveOffer = undefined;
            this.couponData = {
              "charged-currency": result.currency,
              "coupons-total": total,
              "coupon-id": result.discount?.coupon?.id,
              data: result,
            };
            if (this.discountSuccessMessage) {
              this.discountSuccessMessage = this.discountSuccessMessage.replace(
                "{amount}",
                this.currencyPipe.transform(this.couponData["coupons-total"], this.couponData["charged-currency"].toUpperCase())
              );
            }
            this.setChargedMessage();
          } else {
            this.coupon = undefined;
            this.checkoutError = { type: "coupon_error", message: this.translate.instant("checkout.coupon_not_found") };
          }
        },
        error: (error) => {
          let message = error ? this._helperService.retrieveErrorMessage(error) : this.translate.instant("checkout.coupon_not_found");
          if (this.couponParam) message = this.translate.instant("checkout.coupon_does_not_apply", { code });
          this.checkoutError = { type: "coupon_error", message: message };
          this.applyingCoupon = false;
          this.loadingPrices = false;
          this.coupon = undefined;
        },
      });
    }
  }

  applyHardwareCoupon(conditionChecked = false, selectedPlan = this.selectedS1PlusPlan) {
    if (conditionChecked || (this.coupons && this.coupons.length)) {
      let yearCoupons = this.coupons.filter((cpn) => cpn.toLocaleLowerCase().includes("annual"));
      let monthCoupons = this.coupons.filter((cpn) => cpn.toLocaleLowerCase().includes("monthly"));
      if (yearCoupons && yearCoupons.length) {
        let splitYearCoupons = yearCoupons[0].split("_");
        if (splitYearCoupons && splitYearCoupons.length) {
          let discountPercentYear = splitYearCoupons[splitYearCoupons.length - 1];
          if (discountPercentYear) {
            this.discountedAnnualDisplayPrice = ((this.s1PlusPrices["annual"].price * (100 - parseInt(discountPercentYear))) / 100).toFixed(2);
            this.discountedAnnualMonthlyDisplayPrice = ((this.s1PlusPrices["annual"].price * (100 - parseInt(discountPercentYear))) / 100 / 12).toFixed(2);
          }
        }
      }
      if (monthCoupons && monthCoupons.length) {
        let splitMonthCoupons = monthCoupons[0].split("_");
        if (splitMonthCoupons && splitMonthCoupons.length) {
          let discountPercentMonth = splitMonthCoupons[splitMonthCoupons.length - 1];
          if (discountPercentMonth) {
            this.discountedMonthlyDisplayPrice = ((this.s1PlusPrices["monthly"].price * (100 - parseInt(discountPercentMonth))) / 100).toFixed(2);
          }
        }
      }
      let selectedCoupon = this.coupons.filter((cpn) => cpn.toLocaleLowerCase().includes(selectedPlan.toLocaleLowerCase()));
      if (selectedCoupon && selectedCoupon.length) {
        this.coupon = selectedCoupon[0].replace(/_/g, "");
      }
    }
  }

  displayDiscountedOfferPrice() {
    if (this.productTitle == environment.studioOnePlusBrandName) {
      let selectedCoupon = this.activeOffers
        .filter(
          (cpn) =>
            cpn.applies_to_products &&
            (cpn.applies_to_products.includes(environment.stripe.subscription_product.annual) || cpn.applies_to_products.includes(environment.stripe.subscription_product.monthly))
        )
        .sort((a, b) => a.priority - b.priority);
      if (selectedCoupon && selectedCoupon.length) {
        this.selectedActiveOffer = selectedCoupon[0];
        this.applicableActiveOffer = selectedCoupon[0];

        let startDate = selectedCoupon[0].start_date;
        let endDate = selectedCoupon[0].end_date;
        let currentDate = moment().unix();
        if (selectedCoupon[0].percentage > 0) {
          let yearCoupons = selectedCoupon.filter((cp) => cp.applies_to_products.includes(environment.stripe.subscription_product.annual));
          let monthCoupons = selectedCoupon.filter((cp) => cp.applies_to_products.includes(environment.stripe.subscription_product.monthly));
          if (selectedCoupon[0].coupon_type === "date_range") {
            if (currentDate > startDate && (currentDate < endDate || !endDate)) {
              if (yearCoupons && yearCoupons.length) {
                this.discountedAnnualDisplayPrice = ((this.s1PlusPrices["annual"].price * (100 - yearCoupons[0].percentage)) / 100).toFixed(2);
                this.applicableActiveOffer.discountedAnnualDisplayPrice = ((this.s1PlusPrices["annual"].price * (100 - yearCoupons[0].percentage)) / 100).toFixed(2);
                this.discountedAnnualMonthlyDisplayPrice = ((this.s1PlusPrices["annual"].price * (100 - yearCoupons[0].percentage)) / 100 / 12).toFixed(2);
                this.applicableActiveOffer.discountedAnnualMonthlyDisplayPrice = ((this.s1PlusPrices["annual"].price * (100 - yearCoupons[0].percentage)) / 100 / 12).toFixed(2);
              }
              if (monthCoupons && monthCoupons.length) {
                this.discountedMonthlyDisplayPrice = ((this.s1PlusPrices["monthly"].price * (100 - monthCoupons[0].percentage)) / 100).toFixed(2);
                this.applicableActiveOffer.discountedMonthlyDisplayPrice = ((this.s1PlusPrices["monthly"].price * (100 - monthCoupons[0].percentage)) / 100).toFixed(2);
              }
            }
          } else {
            if (yearCoupons && yearCoupons.length) {
              this.discountedAnnualDisplayPrice = ((this.s1PlusPrices["annual"].price * (100 - yearCoupons[0].percentage)) / 100).toFixed(2);
              this.applicableActiveOffer.discountedAnnualDisplayPrice = ((this.s1PlusPrices["annual"].price * (100 - yearCoupons[0].percentage)) / 100).toFixed(2);
              this.discountedAnnualMonthlyDisplayPrice = ((this.s1PlusPrices["annual"].price * (100 - yearCoupons[0].percentage)) / 100 / 12).toFixed(2);
              this.applicableActiveOffer.discountedAnnualMonthlyDisplayPrice = ((this.s1PlusPrices["annual"].price * (100 - yearCoupons[0].percentage)) / 100 / 12).toFixed(2);
            }
            if (monthCoupons && monthCoupons.length) {
              this.discountedMonthlyDisplayPrice = ((this.s1PlusPrices["monthly"].price * (100 - monthCoupons[0].percentage)) / 100).toFixed(2);
              this.applicableActiveOffer.discountedMonthlyDisplayPrice = ((this.s1PlusPrices["monthly"].price * (100 - monthCoupons[0].percentage)) / 100).toFixed(2);
            }
          }
        }
      }
    }
  }

  applyActiveOffer(conditionChecked = false) {
    if (environment.features.active_offers && (conditionChecked || (this.activeOffers && this.activeOffers.length)) && (this.selectedS1PlusPlan || this.selectedProduct)) {
      let selectedCoupon = this.activeOffers
        .filter((cpn) => {
          if (this.productTitle == environment.studioOnePlusBrandName) {
            return cpn.applies_to_products && cpn.applies_to_products.includes(environment.stripe.subscription_product[this.selectedS1PlusPlan]);
          } else {
            return cpn.applies_to_products && this.selectedProduct && cpn.applies_to_products.includes(this.selectedProduct.stripeProductId);
          }
        })
        .sort((a, b) => a.priority - b.priority);
      if (selectedCoupon && selectedCoupon.length) {
        // If the user arrived with a promotion coupon (e.g. a registered-product "Special Offers"
        // deal) that is at least as good as this auto-apply offer, favor the promotion instead.
        const price = this.productTitle == environment.studioOnePlusBrandName ? this.s1PlusPrices[this.selectedS1PlusPlan].price : this.selectedProduct.price;
        if (this.promotionBeatsActiveOffer(selectedCoupon[0].percentage, price)) {
          this.selectedActiveOffer = undefined;
          this.coupon = this.promotionCoupon.code;
          this.couponApplied = {
            code: this.promotionCoupon.code,
            discount_percent: this.promotionCoupon.percent_off,
            amount_off: this.getPromotionDiscountAmount(price),
            title: selectedCoupon[0].display_message,
          };
          this.applyCoupon(this.promotionCoupon.code);
          this.setTotalAmountPayable();
          return;
        }
        this.selectedActiveOffer = selectedCoupon[0];
        let startDate = selectedCoupon[0].start_date;
        let endDate = selectedCoupon[0].end_date;
        let currentDate = moment().unix();
        if (selectedCoupon[0].coupon_type === "date_range") {
          if (currentDate > startDate && currentDate < endDate) {
            this.coupon = atob(selectedCoupon[0].code); //Trasnforming to normal coupon string
          }
        } else {
          this.coupon = atob(selectedCoupon[0].code); //Trasnforming to normal coupon string
        }
        this.couponApplied = {
          code: this.coupon,
          discount_percent: selectedCoupon[0].percentage,
          coupon_id: selectedCoupon[0].coupon_id,
          amount_off:
            this.productTitle == environment.studioOnePlusBrandName
              ? (this.s1PlusPrices[this.selectedS1PlusPlan].price * selectedCoupon[0].percentage) / 100
              : (this.selectedProduct.price * selectedCoupon[0].percentage) / 100,
          title: selectedCoupon[0].display_message,
        };
        // this.couponData is set and calculated correctly in applyCoupon.
        // When auto-apply is set that couponData isn't set set after an address is entered
        // the tax calculation is done off the non-discounted price as opposed of the discounted price
        // I think this is a temporaty fix as I am having to add promo_code to the coupon metadata.
        // we might have to re-evaluate later.
        this.applyCoupon(selectedCoupon[0].promo_code);
      }
    }
    this.setTotalAmountPayable();
  }

  public isAutoOfferApplicable() {
    if (this.activeOffers && this.activeOffers.length && this.selectedS1PlusPlan) {
      let selectedCoupon = this.activeOffers
        .filter((cpn) => cpn.applies_to_products && cpn.applies_to_products.includes(environment.stripe.subscription_product[this.selectedS1PlusPlan]))
        .sort((a, b) => a.priority - b.priority);
      return selectedCoupon && selectedCoupon.length > 0 ? true : false;
    }
    return false;
  }

  // Resolves the promotion coupon's discount to an absolute amount (in currency units) for the
  // given price, so it can be compared against an auto-apply active offer's percentage discount.
  private getPromotionDiscountAmount(price: number): number {
    if (!this.promotionCoupon || !price) return 0;
    if (this.promotionCoupon.percent_off) return (price * this.promotionCoupon.percent_off) / 100;
    if (this.promotionCoupon.amount_off) return this.promotionCoupon.amount_off / 100; // Stripe cents -> units
    return 0;
  }

  // True when a carried-over promotion coupon is at least as good as the active offer (tie favors
  // the promotion). Returns false when there is no promotion coupon, leaving the active offer intact.
  private promotionBeatsActiveOffer(activeOfferPercentage: number, price: number): boolean {
    if (!this.promotionCoupon || !price) return false;
    return this.getPromotionDiscountAmount(price) >= (price * (activeOfferPercentage || 0)) / 100;
  }

  applyCouponParam() {
    if (this.couponParam) this.applyCoupon(this.couponParam);
  }

  removeCoupon() {
    this.couponData = undefined;
    this.coupon = "";
    this.discountSuccessMessage = "";
    this.showCouponField = false;
    this.selectedActiveOffer = undefined;
    this.setChargedMessage();
    this.couponApplied = undefined;
  }

  setS1PlusPrices(term, id, price, currency) {
    if (!this.s1PlusPrices) this.s1PlusPrices = {};
    if (term === "annual") {
      this.s1PlusPrices.annual = {
        id: id,
        price: price,
        currency: currency,
        formatted_price: null,
        tax: null,
        tax_rate: null,
        productId: environment.product_ids.sphere_annual,
        title: environment.studioOnePlusBrandName + " (Annual)",
        stripeProductId: environment.stripe.subscription_product.annual,
        image: this.contentService.logos[this.userService.appId][this.userService.activeTheme].subscriptionBoxAnnual,
      };
    } else if (term === "monthly") {
      this.s1PlusPrices.monthly = {
        id: id,
        price: price,
        currency: currency,
        formatted_price: null,
        tax: null,
        tax_rate: null,
        productId: environment.product_ids.sphere_monthly,
        title: environment.studioOnePlusBrandName + " (Monthly)",
        stripeProductId: environment.stripe.subscription_product.monthly,
        image: this.contentService.logos[this.userService.appId][this.userService.activeTheme].subscriptionBoxMonthly,
      };
    } else {
      this.s1PlusPrices.hybrid_upgrade = {
        id: id,
        price: price,
        currency: currency,
        formatted_price: null,
        tax: null,
        tax_rate: null,
        // productId: environment.product_ids.sphere_monthly,
        title: environment.studioOnePlusBrandName + " Annual Upgrade",
        stripeProductId: environment.stripe.subscription_product.hybrid_upgrade,
        image: this.contentService.logos[this.userService.appId][this.userService.activeTheme].subscriptionBoxAnnual,
      };
    }
    this.selectedPrice = this.s1PlusPrices[this.selectedS1PlusPlan];
    if (this.s1PlusPrices.annual && this.s1PlusPrices.monthly) this.setTotalAmountPayable();
  }

  setSelectedProduct(product, stripeProductId, price, currency) {
    this.selectedProduct = {
      id: stripeProductId,
      price: price,
      currency: currency,
      formatted_price: null,
      tax: null,
      tax_rate: null,
      title: product.title,
      productId: product.id,
      upgradeProductId: product.toPackage_id,
      stripeProductId: product.stripeProductId,
    };
    this.totalCharge = undefined;
    this.couponData = undefined;
    this.applyActiveOffer();
  }

  getActiveOffers() {
    this.loadingPrices = true;
    this.userService.getActiveOffers().subscribe({
      next: (result: any) => {
        this.loadingPrices = false;
        if (result && result.length) {
          this.activeOffers = result;
          this.offersRetrieved = true;
          this.displayDiscountedOfferPrice();
          this.applyActiveOffer(true);
        }
      },
      error: (error: any) => {
        this.applyingCoupon = false;
        this.loadingPrices = false;
        this.coupon = undefined;
      },
    });
  }

  public isCurrencySupported(currency) {
    if (this.supportedCurrencies && this.supportedCurrencies.length && currency) {
      return this.supportedCurrencies.includes(currency);
    }
    return false;
  }

  addCardToCustomer(setCardAsSubscriptionPaymentMethod?) {
    return new Promise((resolve, reject) => {
      let paymentMethod = {
        address: {
          line1: this.billingInfo.address,
          city: this.billingInfo.city,
          state: this.billingInfo.state,
          country: this.billingInfo.country,
          postal_code: this.billingInfo.zip,
        },
        is_tax_inclusive: this.isTaxIncluded,
        test_clock: environment.stripe.test_clock,
      };
      if (this.newPaymentMethod) {
        this.userService.getCardClientSecret(paymentMethod).subscribe({
          next: (result: any) => {
            if (result?.success && result.data) {
              this.clientSecret = result.data.client_secret;

              this.stripe
                .confirmCardSetup(result.data.client_secret, {
                  payment_method: this.newPaymentMethod,
                })
                .then((response) => {
                  if (response.setupIntent) {
                    if (setCardAsSubscriptionPaymentMethod) {
                      this.userService.setDefaultPaymentMethod(response.setupIntent.payment_method).subscribe({
                        next: () => {
                          resolve(response.setupIntent.payment_method);
                        },
                        error: (error) => {
                          reject(new Error(error?.message || this.translate.instant("checkout.failed_set_default_payment_method")));
                        },
                      });
                    } else {
                      resolve(response.setupIntent.payment_method);
                    }
                  } else if (response.error) {
                    this.checkoutError = {
                      type: "add_card",
                      message: response.error.message,
                    };
                    reject(new Error(response.error.message || this.translate.instant("checkout.unknown_setup_error")));
                  }
                })
                .catch((error) => {
                  this.checkoutError = {
                    type: "add_card",
                    message: error.message,
                  };
                  reject(new Error(error?.message || this.translate.instant("checkout.failed_confirm_card_setup")));
                });
            } else {
              reject(new Error(this.translate.instant("checkout.failed_retrieve_card_client_secret")));
            }
          },
          error: (error) => {
            this.checkoutError = {
              type: "add_card",
              message: error?.message || this.translate.instant("checkout.failed_get_card_client_secret"),
            };
            reject(new Error(error?.message || this.translate.instant("checkout.failed_get_card_client_secret")));
          },
        });
      } else {
        reject(new Error(this.translate.instant("checkout.enter_credit_card_information")));
      }
    });
  }

  public replaceCurrency(currency) {
    if (environment.stripe.replace_currency && currency) {
      let replacedCurrency = environment.stripe.replace_currency.find((cur) => cur.from.toLocaleLowerCase() === currency.toLocaleLowerCase());
      if (replacedCurrency) {
        return replacedCurrency.to;
      }
    }
    return currency;
  }

  public isNoDecimalCurrency(currency) {
    if (currency) {
      return environment.stripe.no_decimal_currencies.includes(currency.toUpperCase());
    }
    return false;
  }

  public isDivisibleBy100(currency) {
    if (currency) {
      return environment.stripe.divisible_by_100_currencies.includes(currency.toUpperCase());
    }
    return false;
  }

  public isDifferentAddress() {
    if (this.taxedBillingInfo) {
      return Object.keys(this.billingInfo).some((key) => this.billingInfo[key] !== this.taxedBillingInfo[key]);
    }
    return true;
  }

  get isValidCheckout() {
    const hasRequiredBilling = this.allFieldsPresent();
    if (!hasRequiredBilling || this.billingAddressRejected) {
      return false;
    }

    if (this.selectedExistingPaymentMethod) {
      return true;
    }

    if (this.newPaymentMethod) {
      return true;
    }

    return Object.values(this.ccInfo).every((v) => v === true);
  }

  private resetLoadingStateOnError() {
    this.loadingCheckout = false;
    this.loadingPrices = false;
    this.calculatingTax = false;
    this.loadingMsg = undefined;
    this.percentDone = 0;
  }

  public setTotalAmountPayable() {
    let basePrice;
    let discount = 0;
    if (this.scheduleDetails) {
      if (this.selectedS1PlusPlan == "monthly" || this.userService.subDetails?.subscription?.subscription_levels_id == 2) {
        basePrice = 0;
      } else {
        basePrice = this.totalCharge && !this.isTaxIncluded ? this.totalCharge : this.s1PlusPrices ? this.s1PlusPrices.hybrid_upgrade.price : 0;
      }
    } else {
      if (this.productTitle == environment.studioOnePlusBrandName) {
        basePrice = this.totalCharge && !this.isTaxIncluded ? this.totalCharge : this.s1PlusPrices ? this.s1PlusPrices[this.selectedS1PlusPlan].price : 0;
      } else {
        basePrice = (this.totalCharge && !this.isTaxIncluded ? this.totalCharge : this.selectedProduct?.price) || 0;
      }
      if (this.couponData) {
        discount = this.couponData["coupons-total"];
      } else if (this.couponApplied && this.couponApplied.amount_off > 0) {
        discount = this.couponApplied.amount_off;
      }
    }
    let amt = Number((basePrice - discount).toFixed(2));
    this.total = amt;
    return amt;
  }

  performScheduleSubscription(term) {
    this.percentDone = 33;
    this.loadingMsg = "Saving your payment method...";
    //dont do this if they have and have chosen an existing payment method.
    const continueWithPaymentMethod = (paymentMethodId) => {
      this.loadingMsg = "Saving your user details...";
      this.percentDone = 68;
      this.userService.getUserDetails(false).subscribe(() => {
        let args: any = {
          customerId: this.userService.user.stripe_id,
          paymentMethodId: paymentMethodId,
          priceId: this.s1PlusPrices[this.selectedS1PlusPlan].id,
          startDate: this.userService.subDetails.subscription.end_date,
          userId: this.userService.user.id,
          taxCode: environment.tax.tax_code,
          isTaxInclusive: this.isTaxIncluded,
          presonusProductId: this.selectedS1PlusPlan == "monthly" ? environment.product_ids.sphere_monthly : environment.product_ids.sphere_annual,
          planType: this.selectedS1PlusPlan == "monthly" ? "Monthly" : "Hybrid",
          action: "createSchedule",
        };
        this.loadingMsg = "Scheduling your subscription...";
        this.percentDone = 85;
        if (this.currencyIsConverted) {
          delete args.priceId;
          let s1PlusPriceKey = "monthly";
          if (this.selectedProduct?.stripeProductId == environment.stripe.subscription_product.hybrid_upgrade) s1PlusPriceKey = "annual";
          let priceData = {
            product: this.s1PlusPrices[s1PlusPriceKey].stripeProductId,
            currency: this.s1PlusPrices[s1PlusPriceKey].currency,
            unit_amount:
              this.isNoDecimalCurrency(this.s1PlusPrices[s1PlusPriceKey].currency) && !this.isDivisibleBy100(this.s1PlusPrices[s1PlusPriceKey].currency)
                ? this.s1PlusPrices[s1PlusPriceKey].price
                : parseInt((this.s1PlusPrices[s1PlusPriceKey].price * 100).toFixed(0)),
            tax_behavior: "unspecified",
            recurring: {
              interval: s1PlusPriceKey == "annual" ? "year" : "month",
            },
          };
          args.priceData = priceData;
        }
        this.createScheduledSubscription(args).subscribe((result) => {
          this.loadingMsg = "Baking cookies...";
          this.percentDone = 97;
          this.userService.getSubscriptionDetails(true).subscribe((result) => {
            this.loadingCheckout = false;
            this.checkoutComplete$.next({
              product: environment.studioOnePlusBrandName,
            });
          });
        });
      });
    };
    if (this.selectedExistingPaymentMethod) {
      continueWithPaymentMethod(this.selectedExistingPaymentMethod);
    } else {
      this.addCardToCustomer().then((paymentMethodId) => {
        continueWithPaymentMethod(paymentMethodId);
      });
    }
  }

  checkOut() {
    if (!this.cardCountry || this.cardCountry === this.billingInfo.country) {
      if (this.isValidCheckout) {
        if (this.scheduleDetails) {
          this.loadingCheckout = true;
          if (this.selectedS1PlusPlan == "monthly") {
            this.performScheduleSubscription("monthly");
          } else {
            if (this.userService.subDetails.subscription.subscription_levels_id == 2) {
              this.performScheduleSubscription("annual");
            } else {
              // is paying for the annual upgrade.
              this.selectedProduct = this.s1PlusPrices.hybrid_upgrade;
              this.orderCompletedTrackingArgs = {
                siteId: "my_fender",
                quantity: 1,
                paymentMethod: "Credit Card",
                locale: this.billingInfo.country,
                prsId: this.userService.user.id,
                coupon: null,
                discount: null,
                tax: this.taxCalculated,
                total: this.total,
                subtotal: this.total - this.taxCalculated,
                currency: this.activeCurrency,
                currencyCode: this.activeCurrency,
                revenue: parseFloat((this.total - this.taxCalculated).toString()),
                price: parseFloat((this.total - this.taxCalculated).toString()),
              };
              this.purchaseComplete$.pipe(take(1)).subscribe((result) => {
                this.loadingMsg = "Updating your user information...";
                this.percentDone = 80;
                this.userService.getSubscriptionDetails(true).subscribe((result) => {
                  let args: any = {
                    customerId: this.userService.subDetails?.stripe_customer?.id,
                    paymentMethodId: this.userService.paymentMethods[0].id,
                    priceId: this.s1PlusPrices.annual.id,
                    startDate: this.scheduleDetails.annual_upgrade_subscription_start_date,
                    userId: this.userService.user.id,
                    taxCode: environment.tax.tax_code,
                    isTaxInclusive: this.isTaxIncluded,
                    bundleTimeIncrease: 6,
                    presonusProductId: environment.product_ids.sphere_annual,
                    planType: "Hybrid",
                    action: "createSchedule",
                  };
                  if (this.userService.subDetails?.scheduled_stripe_subscription) args.cancelExistingScheduledSubscription = this.userService.subDetails.scheduled_stripe_subscription.id;
                  this.loadingMsg = "Scheduling your subscription...";
                  this.percentDone = 85;

                  if (this.currencyIsConverted) {
                    delete args.priceId;
                    let s1PlusPriceKey = "monthly";
                    if (this.selectedProduct?.stripeProductId == environment.stripe.subscription_product.hybrid_upgrade) s1PlusPriceKey = "annual";
                    let priceData = {
                      product: this.s1PlusPrices[s1PlusPriceKey].stripeProductId,
                      currency: this.s1PlusPrices[s1PlusPriceKey].currency,
                      unit_amount:
                        this.isNoDecimalCurrency(this.s1PlusPrices[s1PlusPriceKey].currency) && !this.isDivisibleBy100(this.s1PlusPrices[s1PlusPriceKey].currency)
                          ? this.s1PlusPrices[s1PlusPriceKey].price
                          : parseInt((this.s1PlusPrices[s1PlusPriceKey].price * 100).toFixed(0)),
                      tax_behavior: "unspecified",
                      recurring: {
                        interval: s1PlusPriceKey == "annual" ? "year" : "month",
                      },
                    };
                    args.priceData = priceData;
                  }
                  this.createScheduledSubscription(args).subscribe((result) => {
                    this.loadingMsg = "Baking cookies...";
                    this.percentDone = 97;
                    this.userService.getSubscriptionDetails(true).subscribe((result) => {
                      this.loadingCheckout = false;
                      this.checkoutComplete$.next({
                        product: environment.studioOnePlusBrandName,
                      });
                    });
                  });
                });
                // now we schedule the annual subscription.
              });
              this.performPurchase("sphere_signup");
            }
          }
        } else {
          this.loadingCheckout = true;
          this.loadingMsg = "10% done...";
          this.percentDone = 10;
          this.loadingMsg = "Please wait while your payment is being processed...";
          let channel: any;
          channel = this.source ? "src_" + this.source : this.productTitle == environment.studioOnePlusBrandName ? "sphere_signup" : "product_upgrade";
          if (this.clientSecret) {
            //Confirming subscription for existing subscription
            this.confirmCardPayment(channel);
          } else {
            let coupon = this.coupon;
            if (this.selectedActiveOffer) coupon = atob(this.selectedActiveOffer.code);
            let discount = 0;
            if (this.couponData) {
              discount = parseFloat(this.couponData["coupons-total"]);
            } else if (this.couponApplied) {
              discount = this.couponApplied.amount_off;
            }
            this.orderCompletedTrackingArgs = {
              siteId: "my_fender",
              quantity: 1,
              paymentMethod: "Credit Card",
              locale: this.billingInfo.country,
              prsId: this.userService.user.id,
              coupon: coupon,
              discount: discount,
              tax: this.taxCalculated,
              total: this.total,
              subtotal: this.total - this.taxCalculated,
              currency: this.activeCurrency,
              currencyCode: this.activeCurrency,
              revenue: parseFloat((this.total - this.taxCalculated).toString()),
              price: parseFloat((this.total - this.taxCalculated).toString()),
            };
            if (this.productTitle == environment.studioOnePlusBrandName) {
              // if its a subscription, create the subscription.
              this.createSubscription(channel);
            } else {
              // if its not subscription, create the payment intent.
              this.performPurchase(channel);
            }
          }
        }
      } else {
        this.checkoutError = {
          type: "add_card",
          message: this.translate.instant("checkout.fill_required_fields_check_card_info"),
        };
      }
    } else {
      this.checkoutError = {
        type: "add_card",
        message: this.translate.instant("checkout.card_billing_address_mismatch"),
      };
      this.addNewPaymentMethod();
    }
  }

  createScheduledSubscription(args) {
    return this.http.post(environment.paeApiUrl + `user/subscription/bundle-updates`, JSON.stringify(args), this._helperService.getHttpOptions());
  }

  cancelScheduledSubscription(args) {
    return this.http.post(environment.paeApiUrl + `user/subscription/bundle-updates`, JSON.stringify({ action: "cancelSchedule", ...args }), this._helperService.getHttpOptions());
  }

  cancelSubscription(args) {
    return this.http.post(environment.paeApiUrl + `user/subscription/bundle-updates`, JSON.stringify({ action: "cancelSubscription", ...args }), this._helperService.getHttpOptions());
  }

  getSelectedPrice() {
    if (this.selectedPrice) {
      return this.selectedPrice;
    }
    if (!this.priceList) {
      return undefined;
    }
    let price = this.priceList.filter((p) => p.currency.toLowerCase() === this.activeCurrency.toLowerCase() && this.selectedProduct.id == p.product)[0];
    if (!price) price = this.priceList.filter((p) => p.currency.toLowerCase() === "usd" && this.selectedProduct.id == p.product)[0];
    return price;
  }

  performPurchase(channel?) {
    this.orderCompletedTrackingArgs.products = [
      {
        price: parseFloat(this.orderCompletedTrackingArgs.subtotal),
        sku: this.selectedProduct.productId,
        productId: this.selectedProduct.productId,
        quantity: 1,
        name: this.selectedProduct.title,
      },
    ];
    let selectedProduct = this.selectedProduct;
    let selectedPrice = this.scheduleDetails ? this.s1PlusPrices.hybrid_upgrade : this.getSelectedPrice();
    let productKey: any;
    if (this.upgradingProduct?.license?.productKeyString) {
      productKey = this.upgradingProduct?.license?.productKeyString;
    } else {
      productKey = this.upgradableProducts?.data?.products?.find((p) => p.stripeProductId === this.selectedProduct.stripeProductId)?.productKeyString ?? null;
    }

    let couponId = this.couponData ? this.couponData["coupon-id"] : "";
    if (!couponId && this.couponApplied && this.couponApplied.coupon_id) couponId = this.couponApplied.coupon_id;
    let purchaseRequest: any = {
      old_product_key: productKey,
      product_id: selectedProduct.productId,
      is_tax_inclusive: this.isTaxIncluded,
      sales_channel: channel,
      currency: selectedProduct.currency,
      price_id: selectedPrice?.id,
      firstName: this.billingInfo.firstName,
      lastName: this.billingInfo.lastName,
      address: {
        line1: this.billingInfo.address || this.userService.subDetails?.stripe_customer?.address?.line1,
        city: this.billingInfo.city || this.userService.subDetails?.stripe_customer?.address?.city,
        state: this.billingInfo.state || this.userService.subDetails?.stripe_customer?.address?.state,
        country: this.billingInfo.country || this.userService.subDetails?.stripe_customer?.address?.country,
        postal_code: this.billingInfo.zip || this.userService.subDetails?.stripe_customer?.address?.postal_code,
      },
      coupon_id: couponId,
    };
    if (this.currencyIsConverted) {
      delete purchaseRequest.price_id;
      purchaseRequest.price_data = {
        product: this.selectedProduct.stripeProductId,
        currency: selectedProduct.currency,
        unit_amount:
          this.isNoDecimalCurrency(selectedProduct.currency) && !this.isDivisibleBy100(selectedProduct.currency) ? selectedProduct.price : parseInt((selectedProduct.price * 100).toFixed(0)),
        tax_behavior: "unspecified",
      };
    }
    this.loadingMsg = "25% done...";
    this.percentDone = 25;
    this.userService.createProductInvoice(purchaseRequest).subscribe({
      next: (result: any) => {
        if (result && result.invoice) {
          this.loadingMsg = "35% done...";
          this.percentDone = 35;
          setTimeout(() => {
            this.checkoutResume(result.invoice, channel);
          }, 3000);
        } else {
          this.resetLoadingStateOnError();
          this.checkoutError = {
            type: "add_card",
            message: this.translate.instant("checkout.fill_required_fields_check_card_info"),
          };
        }
      },
      error: (error) => {
        this.resetLoadingStateOnError();
        if (this._helperService.retrieveErrorMessage(error).includes("combine currencies on a single customer")) {
          this.checkoutError = {
            type: "report_error",
            description: this._helperService.retrieveErrorMessage(error),
            message: this.translate.instant("checkout.currency_mismatch_error"),
          };
        } else {
          this.checkoutError = {
            type: "add_card",
            message: this._helperService.retrieveErrorMessage(error),
          };
        }
      },
    });
  }

  createSubscription(channel?) {
    let selectedPrice = this.s1PlusPrices?.[this.selectedS1PlusPlan];
    if (!selectedPrice) {
      this.resetLoadingStateOnError();
      this.checkoutError = {
        type: "add_card",
        message: this.translate.instant("checkout.fill_required_fields_check_card_info"),
      };
      return;
    }
    let subscribeRequest: any = {
      is_tax_inclusive: this.isTaxIncluded,
      sales_channel: channel,
      currency: selectedPrice.currency,
      firstName: this.billingInfo.firstName,
      lastName: this.billingInfo.lastName,
      price_id: selectedPrice.id,
      address: {
        line1: this.billingInfo.address,
        city: this.billingInfo.city,
        state: this.billingInfo.state,
        country: this.billingInfo.country,
        postal_code: this.billingInfo.zip,
      },
      promotion_code: this.couponData ? this.coupon : "",
    };
    if (environment.stripe.test_clock) subscribeRequest.test_clock = environment.stripe.test_clock;
    if (this.currencyIsConverted) {
      delete subscribeRequest.price_id;
      subscribeRequest.price_data = {
        recurring: {
          interval: this.selectedS1PlusPlan.includes("annual") ? "year" : "month",
        },
        product: environment.stripe.subscription_product[this.selectedS1PlusPlan],
        currency: selectedPrice.currency,
        unit_amount: this.isNoDecimalCurrency(selectedPrice.currency) && !this.isDivisibleBy100(selectedPrice.currency) ? selectedPrice.price : parseInt((selectedPrice.price * 100).toFixed(0)),
        tax_behavior: "unspecified",
      };
    }
    this.userService.stripeSubscribe(subscribeRequest).subscribe(
      (result: any) => {
        if (result && result.invoice) {
          this.loadingMsg = "25% done...";
          this.percentDone = 25;
          setTimeout(() => {
            this.checkoutResume(result.invoice, channel);
          }, 3000);
        } else {
          this.resetLoadingStateOnError();
          this.checkoutError = {
            type: "add_card",
            message: this.translate.instant("checkout.fill_required_fields_check_card_info"),
          };
        }
      },
      (error) => {
        this.resetLoadingStateOnError();
        if (this._helperService.retrieveErrorMessage(error).includes("combine currencies on a single customer")) {
          this.checkoutError = {
            type: "report_error",
            description: this._helperService.retrieveErrorMessage(error),
            message: this.translate.instant("checkout.currency_mismatch_error"),
          };
        } else {
          this.checkoutError = {
            type: "add_card",
            message: this._helperService.retrieveErrorMessage(error),
          };
        }
      }
    );
  }

  checkoutResume(invoiceData, channel) {
    let args: any = { invoice: invoiceData };
    if (this.productTitle == environment.studioOnePlusBrandName) args.transaction_type = "new";
    this.userService[this.productTitle == environment.studioOnePlusBrandName && !this.scheduleDetails ? "subscriptionResume" : "resumeTransaction"](args).subscribe({
      next: (result: any) => {
        if (result) {
          this.loadingMsg = "65% done...";
          this.percentDone = 65;
          if (!result.clientSecret && result.status === "paid") {
            this.clientSecret = "";
            if (this.productTitle == environment.studioOnePlusBrandName && !this.scheduleDetails) {
              this.addCardToCustomer();
              this.clientSecret = "";
              this.subscriptionId = "";
              this.subscriptionComplete();
            } else {
              if (this.scheduleDetails) {
                this.purchaseComplete$.next(true);
              } else {
                this.purchaseComplete();
              }
            }
          } else {
            if (result.subscriptionId) this.subscriptionId = result.subscriptionId;
            this.clientSecret = result.clientSecret;
            this.confirmCardPayment(channel);
          }
        }
      },
      error: (error) => {
        this.resetLoadingStateOnError();
        this.checkoutError = {
          type: "add_card",
          message: this._helperService.retrieveErrorMessage(error),
        };
      },
    });
  }

  confirmCardPayment(channel) {
    this.loadingMsg = "45% done...";
    this.percentDone = 45;
    this.stripe
      .confirmCardPayment(this.clientSecret, {
        payment_method: this.newPaymentMethod || this.selectedExistingPaymentMethod,
      })
      .then((response) => {
        if (response.error) {
          this.loadingMsg = undefined;
          this.percentDone = 0;
          this.loadingCheckout = false;
          this.checkoutError = {
            type: "add_card",
            message: response.error.message ? response.error.message : this.translate.instant("checkout.check_card_info_try_again"),
          };
        } else {
          this.clientSecret = "";
          this.subscriptionId = "";
          if (this.productTitle == environment.studioOnePlusBrandName && !this.scheduleDetails) {
            this.subscriptionComplete();
          } else {
            if (this.scheduleDetails) {
              this.purchaseComplete$.next(true);
            } else {
              this.purchaseComplete();
            }
          }
        }
      })
      .catch((error) => {
        this.loadingCheckout = false;
        this.loadingMsg = undefined;
        this.percentDone = 0;
        this.checkoutError = {
          type: "add_card",
          message: this.translate.instant("checkout.fill_required_fields_check_card_info"),
        };
      });
  }

  purchaseComplete() {
    this.loadingMsg = "85% done...";
    this.percentDone = 85;
    this.clientSecret = "";

    // The purchased product appears in the user's product list only after the
    // Stripe webhook fires, registers the license, and the consumer Lambda
    // returns. The previous code waited a blind 5s, which under-covers cold-start
    // EventBridge delivery (~5–10s observed). Poll the product list and bail out
    // as soon as the new product shows up, with a hard cap so we never hang.
    const expectedProductId = this.selectedProduct?.productId;
    const backoffMs = [1000, 1500, 2000, 2500, 3000, 4000]; // ~14s total cap
    let attempt = 0;

    const finish = () => {
      if (this.orderCompletedTrackingArgs && !this.orderCompletedTrackingArgs?.products) this.orderCompletedTrackingArgs.products = [];
      this.checkoutComplete$.next({
        product: this.productTitle,
        trackingData: this.orderCompletedTrackingArgs,
      });
      if (this.orderCompletedTrackingArgs) this.storage.setItem(StorageKeys.CHECKOUT_COMPLETED_DATA, this.orderCompletedTrackingArgs);
    };

    const poll = () => {
      this.productService.getProducts(true).subscribe({
        next: (result: any) => {
          const products = result?.products || [];
          const found = expectedProductId ? products.some((p: any) => p.id === expectedProductId) : true;
          if (found || attempt >= backoffMs.length) {
            finish();
            return;
          }
          setTimeout(poll, backoffMs[attempt++]);
        },
        error: () => {
          // Don't strand the user on a getProducts error — finish anyway.
          finish();
        },
      });
    };

    setTimeout(poll, backoffMs[attempt++]);
  }

  calculateFreeTimeProgress(startDateStr, endDateStr) {
    // Parse the dates from strings to Date objects
    const startDate: any = new Date(startDateStr);
    const endDate: any = new Date(endDateStr);
    const currentDate: any = new Date();

    // Calculate the total duration and the elapsed duration in milliseconds
    const totalDuration = endDate - startDate;
    const elapsedDuration = currentDate - startDate;

    // Calculate the remaining duration in milliseconds
    const remainingDuration = endDate - currentDate;

    // Convert the remaining duration from milliseconds to days
    const daysLeft = remainingDuration / (1000 * 60 * 60 * 24);

    // Calculate the percentage
    const progressPercentage = (elapsedDuration / totalDuration) * 100;

    return {
      percentage: progressPercentage.toFixed(2), // Returns the percentage rounded to two decimal places
      daysLeft: Math.ceil(daysLeft), // Rounds up to the nearest whole number to ensure a whole day is counted
    };
  }

  subscriptionComplete() {
    this.loadingMsg = "85% done...";
    this.percentDone = 85;
    this.win.setTimeout(() => {
      this.loadingMsg = "90% done...";
      this.percentDone = 90;
      this.userService.getUserDetails(false).subscribe({
        next: (result) => {
          this.loadingMsg = "95% done...";
          this.percentDone = 95;
          this.productService.getProducts(true).subscribe((newProds) => {
            this.orderCompletedTrackingArgs.products = [
              {
                price: parseFloat(this.orderCompletedTrackingArgs.subtotal),
                sku: this.selectedS1PlusPlan == "annual" ? environment.product_ids.sphere_annual : environment.product_ids.sphere_monthly,
                productId: this.selectedS1PlusPlan == "annual" ? environment.product_ids.sphere_annual : environment.product_ids.sphere_monthly,
                quantity: 1,
                name: "Studio One Plus",
              },
            ];
            this.orderCompletedTrackingArgs.subProvider = this.userService.user.subscription?.provider;
            this.orderCompletedTrackingArgs.subStartDate = this.userService.user.subscription?.start_date;
            this.orderCompletedTrackingArgs.subEndDate = this.userService.user.subscription?.end_date;
            this.orderCompletedTrackingArgs.subType = this.userService.user.subscription?.type;
            this.orderCompletedTrackingArgs.prsSubStatus = this.userService.user.subscription?.status;
            this.percentDone = 100;
            if (this.orderCompletedTrackingArgs && !this.orderCompletedTrackingArgs?.products) this.orderCompletedTrackingArgs.products = [];
            this.checkoutComplete$.next({
              product: environment.studioOnePlusBrandName,
              trackingData: this.orderCompletedTrackingArgs,
            });
            if (this.orderCompletedTrackingArgs) this.storage.setItem(StorageKeys.CHECKOUT_COMPLETED_DATA, this.orderCompletedTrackingArgs);
          });
        },
        error: (error) => {
          this.loadingCheckout = false;
          this.checkoutError = error;
          this.loadingMsg = undefined;
        },
      });
    }, 5000);
  }

  deletePaymentMethod(id) {
    return this.http.delete(environment.paeApiUrl + `user/paymentmethods/${id}`, this._helperService.getHttpOptions());
  }

  getCharge(id) {
    return this.http.get(environment.paeApiUrl + `user/charge/${id}`, this._helperService.getHttpOptions());
  }

  tearDown() {
    this.activeFinalPaymentAmount = undefined;
    this.calculatingTax = false;
    this.total = 0;
    this.selectedPrice = undefined;
    this.selectedProduct = undefined;
    this.taxCalculated = undefined;
    this.total = 0;
    this.totalCharge = undefined;
    this.checkoutError = undefined;
    this.selectedActiveOffer = undefined;
    this.activeOffers = undefined;
    this.offersRetrieved = false;
    this.scheduleDetails = undefined;
    this.selectedProductUpgrade = undefined;
    this.loadingMsg = undefined;
    this.percentDone = 0;
    this.removeCoupon();
    this.billingInfo = {
      firstName: "",
      lastName: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      country: this.userService.user?.country || this.userService.user?.detectedCountry || "US",
    };
  }
}
